import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageToggle } from '../components/LanguageToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nutrifit-portal/artifacts/s4srivcq_Logo%20Icono%20Solo%20Nutriquimia%20SVG.svg";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '', password: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      toast.success(`${t('welcome')} ${result.name}!`);
      navigateByUserType(result.user_type);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const navigateByUserType = (userType) => {
    switch (userType) {
      case 'admin': navigate('/admin'); break;
      case 'distributor': navigate('/distributor'); break;
      case 'client': navigate('/client'); break;
      default: navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10 relative" style={{ background: 'linear-gradient(160deg, #f8faf6, #edf3e8, #f5f7f3)' }} data-testid="login-page">
      {/* Language */}
      <div className="absolute top-5 right-5 z-10">
        <LanguageToggle variant="default" />
      </div>

      <div className="w-full max-w-sm">
        {/* Logo + Brand */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-20 h-20 mb-4">
            <img src={LOGO_URL} alt="Nutriquimia" className="w-full h-full object-contain" data-testid="login-logo" />
          </div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase mt-1" style={{ color: '#467322' }}>Nutriquimia</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-black/[0.06] p-7">
          <h2 className="text-xl font-bold text-gray-900 text-center mb-0.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Iniciar Sesión
          </h2>
          <p className="text-xs text-gray-400 text-center mb-6">
            Ingresa tus credenciales
          </p>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <Label className="text-[11px] text-gray-400 font-semibold mb-1 block">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <Input type="email" name="email" value={formData.email} onChange={handleInputChange}
                  placeholder="Correo Electrónico" required
                  className="h-11 pl-9 bg-gray-50/80 border border-gray-200/60 focus:border-[#467322] focus:ring-1 focus:ring-[#467322]/15 rounded-xl text-sm" data-testid="input-email" />
              </div>
            </div>

            <div>
              <Label className="text-[11px] text-gray-400 font-semibold mb-1 block">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <Input type={showPassword ? 'text' : 'password'} name="password" value={formData.password}
                  onChange={handleInputChange} placeholder="Contraseña" required
                  className="h-11 pl-9 pr-9 bg-gray-50/80 border border-gray-200/60 focus:border-[#467322] focus:ring-1 focus:ring-[#467322]/15 rounded-xl text-sm" data-testid="input-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" data-testid="toggle-password">
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={loading}
              className="w-full h-11 text-white text-sm font-bold rounded-xl transition-all duration-300 hover:-translate-y-0.5 mt-1 group"
              style={{ background: '#467322' }}
              data-testid="submit-button">
              {loading ? t('loading') : 'Iniciar Sesión'}
              {!loading && <ArrowRight size={15} className="ml-1.5 group-hover:translate-x-1 transition-transform" />}
            </Button>

            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-xs font-semibold hover:underline transition-colors"
                style={{ color: '#467322' }}
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
